import React, { FC, MouseEvent, useMemo, useState } from 'react';
import { Skeleton, Typography, Tag, Tooltip, Button, Modal } from 'antd';
import { DownloadOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { requestDelete, requestDownload } from 'store/admin/dump/slice';
import { useTranslation } from 'react-i18next';
import { ExportFolder } from '../../types';
import { StyledListRow, StyledIconsBlock } from './styles';

interface UsersListRowProps {
  item: ExportFolder;
  selectedItem: ExportFolder | null;
  isListLoaded: boolean | null;
  onItemClick: (item: ExportFolder) => void;
}

const Row: FC<UsersListRowProps> = ({ item, onItemClick, selectedItem, isListLoaded }) => {
  const { Text } = Typography;
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();

  const isCompleted = item.exportStatus === 'COMPLETED';

  const createdAt = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(item.createdAt));
    } catch {
      return item.createdAt || '';
    }
  }, [item.createdAt]);

  const statusTag = useMemo(() => {
    const map: Record<string, 'success' | 'processing' | 'default'> = {
      COMPLETED: 'success',
      IN_PROGRESS: 'processing',
      AWAITING: 'default',
    };
    const I18KEY: Record<string, 'dump.status.COMPLETED' | 'dump.status.IN_PROGRESS' | 'dump.status.AWAITING'> = {
      COMPLETED: 'dump.status.COMPLETED',
      IN_PROGRESS: 'dump.status.IN_PROGRESS',
      AWAITING: 'dump.status.AWAITING',
    };
    const status = I18KEY[item.exportStatus];
    const label = t(status);
    return <Tag color={map[item.exportStatus] || 'default'}>{label}</Tag>;
  }, [item.exportStatus]);

  const handleClick = (e: MouseEvent) => {
    if (!isCompleted) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onItemClick(item);
  };

  const onDownloadFile = () => {
    dispatch(requestDownload({ key: item.name }));
  };

  const onDeleteFile = () => {
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(requestDelete({ key: item.name }));
    setIsModalOpen(false);
  };

  return (
    <>
      <StyledListRow
        $isSelected={item.name === selectedItem?.name}
        onClick={handleClick}
        aria-disabled={!isCompleted}
        style={!isCompleted ? { cursor: 'not-allowed', opacity: 0.7 } : undefined}
      >
        <Skeleton loading={!isListLoaded} active>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: 4 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong style={{ display: 'block' }}>
                {item.name}
              </Text>
              <Text type="secondary" style={{ display: 'block' }}>
                {createdAt}
              </Text>
            </div>

            {statusTag}

            <StyledIconsBlock>
              <Tooltip title={isCompleted ? t('dump.download') : t('dump.fileIsntPrepared')}>
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCompleted) onDownloadFile();
                  }}
                  disabled={!isCompleted}
                />
              </Tooltip>
              <Tooltip title={t('dump.deleteModal.delete')} color="red">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFile();
                  }}
                />
              </Tooltip>
            </StyledIconsBlock>
          </div>
        </Skeleton>
      </StyledListRow>
      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            {t('dump.deleteModal.title')}
          </span>
        }
        visible={isModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setIsModalOpen(false)}
        okText={t('dump.deleteModal.delete')}
        cancelText={t('dump.deleteModal.cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>
          {t('dump.deleteModal.message')} <b>{item.name}</b>?
        </p>
      </Modal>
    </>
  );
};

export default Row;
